import React, { useState } from "react";
import { ArrowUpIcon, BarChart3Icon, PieChartIcon } from "lucide-react";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../shadcn/ui/card";
import { cn } from "../utils";

const OccupancyRateCard = ({ className, data = {}, loading = false }) => {
  const [activeTab, setActiveTab] = useState("Chart");

  const safeData = data || {};
  const { totalOccupancyRate = 0, occupancyChange = "+0%", isOccupancyPositive = true, bookingStats = null } = safeData;

  const getMonthlyData = () => {
    if (!bookingStats?.monthlyOccupancyRates) return [];
    const monthNames = {
      JANUARY: "Jan",
      FEBRUARY: "Feb",
      MARCH: "Mar",
      APRIL: "Apr",
      MAY: "May",
      JUNE: "Jun",
      JULY: "Jul",
      AUGUST: "Aug",
      SEPTEMBER: "Sep",
      OCTOBER: "Oct",
      NOVEMBER: "Nov",
      DECEMBER: "Dec",
    };
    return Object.entries(bookingStats.monthlyOccupancyRates)
      .filter(([, rate]) => rate > 0)
      .map(([month, rate]) => ({ month: monthNames[month] || month, rate: Math.round(rate * 100) }));
  };

  if (loading) {
    return (
      <Card className={cn("border shadow-none", className)}>
        <CardHeader>
          <div className="animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-32 mb-2"></div>
            <div className="h-4 bg-slate-200 rounded w-24 mb-4"></div>
          </div>
        </CardHeader>
        <CardContent className="h-[280px] flex items-center justify-center">
          <div className="text-sm text-slate-500">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  const monthlyData = getMonthlyData();
  const maxRate = Math.max(...monthlyData.map(d => d.rate), 100);
  const chartHeight = 200;

  return (
    <Card className={cn("border shadow-none", className)}>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg font-semibold">Occupancy Rate</CardTitle>
            <CardDescription>Space utilization trends</CardDescription>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{totalOccupancyRate}%</p>
            <p
              className={`text-sm flex items-center justify-end ${isOccupancyPositive ? "text-green-500" : "text-red-500"}`}
            >
              <ArrowUpIcon className="mr-1 w-4 h-4" />
              {occupancyChange}
            </p>
          </div>
        </div>
        <div className="mt-4">
          <div className="inline-flex p-1 rounded-lg bg-slate-100">
            <button
              onClick={() => setActiveTab("Chart")}
              className={`px-3 py-1 text-sm rounded-md transition-all flex items-center gap-1 ${activeTab === "Chart" ? "bg-white shadow-sm" : "text-slate-600"}`}
            >
              <BarChart3Icon className="h-3 w-3" />
              Chart
            </button>
            <button
              onClick={() => setActiveTab("Stats")}
              className={`px-3 py-1 text-sm rounded-md transition-all flex items-center gap-1 ${activeTab === "Stats" ? "bg-white shadow-sm" : "text-slate-600"}`}
            >
              <PieChartIcon className="h-3 w-3" />
              Stats
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="h-[280px]">
        {activeTab === "Chart" && (
          <div className="h-full flex overflow-hidden">
            {monthlyData.length > 0 ? (
              <>
                <div className="text-xs text-slate-500 flex flex-col justify-between h-full pt-4 pb-[2.5rem] pr-3 border-r border-dashed min-w-[50px]">
                  {[100, 75, 50, 25, 0].map((val, i) => (
                    <span key={i} className="text-right leading-none transform translate-y-1/2">
                      {val}%
                    </span>
                  ))}
                </div>
                <div className="flex-1 overflow-x-auto overflow-y-hidden">
                  <div
                    className="flex items-end px-4 gap-2 h-full pt-4 pb-2"
                    style={{ minWidth: `${monthlyData.length * 60}px` }}
                  >
                    {monthlyData.map((item, index) => {
                      const heightPercent = Math.max((item.rate / maxRate) * 100, 1);
                      return (
                        <div
                          key={item.month}
                          className="flex flex-col items-center group h-full justify-end"
                          style={{ width: "50px", minWidth: "50px" }}
                        >
                          <div className="w-full flex-1 flex items-end justify-center mb-2">
                            <div
                              className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t transition-all duration-300 hover:from-blue-600 hover:to-blue-500 cursor-pointer relative"
                              style={{ height: `${heightPercent}%`, minHeight: "2px" }}
                            >
                              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                                {item.rate}%
                              </div>
                            </div>
                          </div>
                          <span className="text-xs text-slate-500 truncate w-full text-center h-6">{item.month}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center text-slate-500">
                  <BarChart3Icon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No occupancy data available</p>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "Stats" && (
          <div className="h-full flex flex-col justify-center">
            {bookingStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{bookingStats.totalBookings || 0}</p>
                    <p className="text-sm text-slate-500">Total Bookings</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{bookingStats.totalActiveBookings || 0}</p>
                    <p className="text-sm text-slate-500">Active Bookings</p>
                  </div>
                </div>
                <div className="p-4 bg-gradient-to-r from-blue-50 to-teal-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Average Occupancy</p>
                      <p className="text-3xl font-bold text-blue-600">{totalOccupancyRate}%</p>
                    </div>
                    <div className="w-20 h-20 rounded-full border-8 border-blue-500 flex items-center justify-center">
                      <span className="text-lg font-bold text-blue-600">{totalOccupancyRate}%</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-slate-500">
                <PieChartIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No statistics available</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OccupancyRateCard;
